// src/admin/pages/Submissions.jsx
import React, { useState, useEffect } from 'react';
import '../../admin/styles/Submissions.css';        // CSS 경로 확인
import { db } from '../../common/firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
} from 'firebase/firestore/lite';

// ─── 상세보기 모달 컴포넌트 ─────────────────────────
function SubmissionModal({ item, onClose }) {
  if (!item) return null;
  const dateStr = item.createdAt?.toDate
    ? item.createdAt.toDate().toLocaleString()
    : '';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h3>문의 상세 정보</h3>
        <dl>
          <dt>이름:</dt>
          <dd>{item.name}</dd>
          <dt>이메일:</dt>
          <dd>{item.email}</dd>
          <dt>회사/전화:</dt>
          <dd>{item.company || '-'}{item.phone ? ` / ${item.phone}` : ''}</dd>
          <dt>제목:</dt>
          <dd>{item.subject}</dd>
          <dt>메시지:</dt>
          <dd className="message">{item.message}</dd>
          <dt>날짜:</dt>
          <dd>{dateStr}</dd>
        </dl>
      </div>
    </div>
  );
}

export default function Submissions() {
  const [items, setItems]             = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage]               = useState(1);
  const [perPage, setPerPage]         = useState(10);
  const [filterSubject, setFilterSubject] = useState('');
  const [modalItem, setModalItem]     = useState(null);

  // Firestore에서 문의 내역 불러오기
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const snap = await getDocs(collection(db, 'contacts'));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // 최신순 정렬
    data.sort((a, b) => {
      const tA = a.createdAt?.seconds || 0;
      const tB = b.createdAt?.seconds || 0;
      return tB - tA;
    });
    setItems(data);
    setPage(1);
    setSelectedIds(new Set());
  };

  // 문서를 읽음 처리 (checked=true)
  const markAsRead = async (id) => {
    const ref = doc(db, 'contacts', id);
    await setDoc(ref, { checked: true }, { merge: true });
    setItems(prev =>
      prev.map(r => (r.id === id ? { ...r, checked: true } : r))
    );
  };

  // 필터링할 subject 옵션 목록
  const subjectOptions = Array.from(
    new Set(items.map(i => i.subject).filter(s => s))
  );

  // 필터 및 페이징 적용
  const filteredItems = items.filter(
    i => !filterSubject || i.subject === filterSubject
  );
  const totalPages     = Math.ceil(filteredItems.length / perPage);
  const startIndex     = (page - 1) * perPage;
  const displayedItems = filteredItems.slice(startIndex, startIndex + perPage);

  // 체크 토글
  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // 전체선택 / 해제 / 삭제
  const selectAll      = () => setSelectedIds(new Set(displayedItems.map(i => i.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const deleteSelected = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm('선택한 항목을 정말 삭제할까요?')) return;
    await Promise.all(
      Array.from(selectedIds).map(id => deleteDoc(doc(db, 'contacts', id)))
    );
    await fetchData();
  };

  return (
    <div className="submissions">
      <h2>문의 내역</h2>

      {/* 툴바 */}
      <div className="toolbar">
        <button onClick={selectAll}>전체선택</button>
        <button onClick={clearSelection}>선택해제</button>
        <button onClick={deleteSelected}>선택삭제</button>

        {/* subject 필터 */}
        <select className="subject-filter"
          value={filterSubject}
          onChange={e => { setFilterSubject(e.target.value); setPage(1); }}
        >
          <option value="">전체 문의</option>
          {subjectOptions.map(sub => (
            <option key={sub} value={sub}>{sub}</option>
          ))}
        </select>

        {/* 페이지 크기 선택 */}
        <select
          value={perPage}
          onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
        >
          {[10, 20, 50, 100].map(n => (
            <option key={n} value={n}>{n}개 보기</option>
          ))}
        </select>
      </div>

      {/* 테이블 */}
      <table>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={e => e.target.checked ? selectAll() : clearSelection()}
                checked={
                  displayedItems.length > 0 &&
                  displayedItems.every(i => selectedIds.has(i.id))
                }
              />
            </th>
            <th>이름</th>
            <th>이메일</th>
            <th>회사/전화</th>
            <th>제목</th>
            <th>메시지</th>
            <th>날짜</th>
            <th>읽음</th>
          </tr>
        </thead>
        <tbody>
          {displayedItems.map(item => {
            const dateStr = item.createdAt?.toDate
              ? item.createdAt.toDate().toLocaleString()
              : '';
            const isUnread = !item.checked;
            return (
              <tr
                key={item.id}
                className={isUnread ? 'unread' : ''}
                onClick={async () => {
                  if (!item.checked) {
                    await markAsRead(item.id);
                  }
                  setModalItem(item);
                }}
              >
                <td data-label="선택">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onClick={e => e.stopPropagation()}
                    onChange={() => toggleSelect(item.id)}
                  />
                </td>
                <td data-label="이름">{item.name}</td>
                <td data-label="이메일">{item.email}</td>
                <td data-label="회사/전화">
                  {item.company || '-'}{item.phone ? ` / ${item.phone}` : ''}
                </td>
                <td data-label="제목">{item.subject}</td>
                <td data-label="메시지">{item.message}</td>
                <td data-label="날짜">{dateStr}</td>
                <td data-label="읽음">{item.checked ? '✔️' : '🔔'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* 페이지 네비게이션 */}
      <div className="pagination">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
        >이전</button>
        <span>{page} / {totalPages}</span>
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
        >다음</button>
      </div>

      {/* 상세보기 모달 */}
      <SubmissionModal
        item={modalItem}
        onClose={() => setModalItem(null)}
      />
    </div>
  );
}
